import React, { useState, useMemo } from 'react';
import { Modal, Stack, Text, TextField, PrimaryButton, DefaultButton, SpinButton, IStackTokens } from '@fluentui/react';
import { CorrectionTimeEntryDto, CreateTimeEntryCorrectionRequest } from '../../../../api/generated/data-contracts';
import { ApiCalls } from '../../../../api/api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { TIME_ENTRY_CORRECTIONS } from '../../../../constants/queryKeys';
import { useCorrectionsStore } from '../../store/correctionsStore';

interface AddCorrectionModalProps {
    isOpen: boolean;
    onDismiss: () => void;
    timeEntry: CorrectionTimeEntryDto;
    timeEntryApprovalId: string; // This would need to be passed from the parent
}

const stackTokens: IStackTokens = { childrenGap: 15 };

export const AddCorrectionModal: React.FC<AddCorrectionModalProps> = ({ isOpen, onDismiss, timeEntry, timeEntryApprovalId }) => {
    const [correctionHours, setCorrectionHours] = useState<number>(0);
    const [comment, setComment] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const queryClient = useQueryClient();
    const addNewCorrection = useCorrectionsStore(store => store.addNewCorrection);

    const { mutate: createCorrectionMutation } = useMutation({
        mutationFn: async (request: CreateTimeEntryCorrectionRequest) => {
            const response = await ApiCalls.timeEntryCorrectionsCreate(request);
            return response.data;
        },
        onSuccess: newCorrection => {
            // Add the new correction to the store as a subrow
            addNewCorrection(timeEntry.id, newCorrection);

            // Also invalidate the query cache to keep it in sync
            queryClient.invalidateQueries({ queryKey: [TIME_ENTRY_CORRECTIONS] });

            onDismiss();
            resetForm();
        },
        onError: error => {
            console.error('Failed to create correction:', error);
            setIsSubmitting(false);
        },
    });

    const resetForm = useMemo(
        () => () => {
            setCorrectionHours(0);
            setComment('');
            setIsSubmitting(false);
        },
        [],
    );

    const handleSubmit = useMemo(
        () => async () => {
            if (!comment.trim()) {
                return; // Comment is required
            }

            setIsSubmitting(true);

            const request: CreateTimeEntryCorrectionRequest = {
                timeEntryId: timeEntry.id,
                timeEntryApprovalId,
                correctionHours,
                comment: comment.trim(),
            };

            createCorrectionMutation(request);
        },
        [comment, correctionHours, timeEntry.id, timeEntryApprovalId, createCorrectionMutation],
    );

    const handleCancel = useMemo(
        () => () => {
            resetForm();
            onDismiss();
        },
        [resetForm, onDismiss],
    );

    return (
        <Modal isOpen={isOpen} onDismiss={handleCancel} isBlocking={false} containerClassName="add-correction-modal">
            <div style={{ padding: '24px', minWidth: '500px' }}>
                <Stack tokens={stackTokens}>
                    <Text variant="xLarge" styles={{ root: { fontWeight: 600 } }}>
                        Add Time Entry Correction
                    </Text>

                    <Stack tokens={{ childrenGap: 8 }}>
                        <Text variant="medium" styles={{ root: { fontWeight: 600 } }}>
                            Time Entry Details
                        </Text>
                        <Text variant="small">
                            <strong>Project:</strong> {timeEntry.projectName}
                        </Text>
                        <Text variant="small">
                            <strong>Task:</strong> {timeEntry.taskName}
                        </Text>
                        <Text variant="small">
                            <strong>User:</strong> {timeEntry.userName}
                        </Text>
                        <Text variant="small">
                            <strong>Date:</strong> {new Date(timeEntry.date).toLocaleDateString()}
                        </Text>
                        <Text variant="small">
                            <strong>Original Hours:</strong> {timeEntry.hours.toFixed(2)}
                        </Text>
                        {timeEntry.comment && (
                            <Text variant="small">
                                <strong>Original Comment:</strong> {timeEntry.comment}
                            </Text>
                        )}
                    </Stack>

                    <Stack tokens={{ childrenGap: 4 }}>
                        <SpinButton
                            label="Correction Hours"
                            value={correctionHours.toString()}
                            onValidate={value => {
                                const numValue = Number.parseFloat(value);
                                if (!Number.isNaN(numValue)) {
                                    setCorrectionHours(numValue);
                                    return numValue.toString();
                                }
                                return correctionHours.toString();
                            }}
                            onIncrement={value => {
                                const numValue = Number.parseFloat(value) + 0.25;
                                setCorrectionHours(numValue);
                                return numValue.toString();
                            }}
                            onDecrement={value => {
                                const numValue = Number.parseFloat(value) - 0.25;
                                setCorrectionHours(numValue);
                                return numValue.toString();
                            }}
                            step={0.25}
                            precision={2}
                            styles={{ root: { width: '200px' } }}
                        />
                        <Text variant="small" styles={{ root: { color: '#605e5c', fontStyle: 'italic' } }}>
                            Enter the change in hours (positive to add, negative to reduce)
                        </Text>
                    </Stack>

                    <TextField
                        label="Correction Comment"
                        placeholder="Enter a comment explaining the correction..."
                        value={comment}
                        onChange={(_, newValue) => setComment(newValue || '')}
                        multiline
                        rows={3}
                        maxLength={1000}
                        required
                        errorMessage={comment.trim() === '' ? 'Comment is required' : undefined}
                    />

                    <Stack horizontal horizontalAlign="end" tokens={{ childrenGap: 10 }}>
                        <DefaultButton text="Cancel" onClick={handleCancel} disabled={isSubmitting} />
                        <PrimaryButton text="Add Correction" onClick={handleSubmit} disabled={isSubmitting || !comment.trim()} />
                    </Stack>
                </Stack>
            </div>
        </Modal>
    );
};
